import { createSignal, createEffect, createMemo, For, Show, onMount } from 'solid-js';
import { useVisibilityPoll } from '../../hooks/useVisibilityPoll';
import { useApi, isOk } from '../../api';
import { useToast } from '../../components/MessageBox';
import Text from '../../components/Text';
import { useTranslation } from '../../hooks/useTranslation';
import BotScoped from '../../components/BotScoped';
import BotStatusBadge from '../../components/BotStatusBadge';
import { useActiveBot } from '../../state/activeBot';
import { type VoteSiteDto, type VoteStateDto, voteOutcomeKey, isPositiveOutcome } from '../../services/votingApi';
import { formatDateTime } from '../../services/dateFormat';
import { refreshLicenses } from '../../hooks/useLicense';
import PluginAutoSettingsForm, { type SettingDef, mergePluginDefaults } from '../../components/PluginAutoSettingsForm';
import Modal from '../../components/Modal';
import styles from '../Voting.module.css';
import { firstError } from '../../api/envelope';

const POLL_MS = 15_000;
const PLUGIN_NAME = 'Voting';

type SiteStatusKind = 'success' | 'pending' | 'failed' | 'stale';

interface UnifiedSiteStatus {
    siteKey: string;
    siteDisplayName: string;
    kind: SiteStatusKind;
    lastVoteUtc: string | null;
    lastOutcome: number;
}

interface PluginSettings {
    plugin: string;
    definitions: SettingDef[];
    values: Record<string, unknown>;
}

type RedemptionState = 'allDone' | 'pending' | 'nothingToRedeem';

export default function VotingBotPage() {
    return (
        <BotScoped>
            <VotingBotInner />
        </BotScoped>
    );
}

function VotingBotInner() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const { botId, bot, refresh: refreshActiveBot } = useActiveBot();

    const [sites, setSites] = createSignal<VoteSiteDto[]>([]);
    const [states, setStates] = createSignal<VoteStateDto[]>([]);
    const [pluginSettings, setPluginSettings] = createSignal<PluginSettings | null>(null);
    const [edits, setEdits] = createSignal<Record<string, unknown>>({});
    const [saving, setSaving] = createSignal(false);
    const [showSettings, setShowSettings] = createSignal(false);
    const [busy, setBusy] = createSignal(false);

    const fetchVoteStates = async () => {
        const id = botId();
        if (!id) return;
        const [sitesRes, statesRes] = await Promise.all([
            api.voting.getSites(),
            api.voting.getState({ botId: id }),
        ]);
        if (isOk(sitesRes.data)) {
            const v = sitesRes.data.value as unknown as { sites?: VoteSiteDto[] } | VoteSiteDto[] | undefined;
            setSites((Array.isArray(v) ? v : (v?.sites ?? [])) as VoteSiteDto[]);
        }
        if (isOk(statesRes.data)) {
            const v = statesRes.data.value as unknown as { states?: VoteStateDto[] } | VoteStateDto[] | undefined;
            setStates((Array.isArray(v) ? v : (v?.states ?? [])) as VoteStateDto[]);
        }
    };

    const fetchPluginSettings = async () => {
        const id = botId();
        if (!id) return;
        const pluginRes = await api.botPlugin.getPluginSettings(id);
        if (isOk(pluginRes.data)) {
            const list = (pluginRes.data.value?.plugins ?? []) as unknown as PluginSettings[];
            const ps = list.find(p => p.plugin.toLowerCase() === PLUGIN_NAME.toLowerCase()) ?? null;
            setPluginSettings(ps);
            if (ps) setEdits(mergePluginDefaults(ps.values ?? {}, ps.definitions));
        }
    };

    const fetchAll = () => Promise.all([fetchVoteStates(), fetchPluginSettings()]);

    createEffect(() => {
        if (botId()) void fetchAll();
    });

    onMount(() => {
        useVisibilityPoll(() => void fetchVoteStates(), POLL_MS);
    });

    const saveSettings = async () => {
        const id = botId();
        if (!id) return;
        setSaving(true);
        const res = await api.botPlugin.savePluginSettings(id, PLUGIN_NAME, { settings: edits() as never });
        setSaving(false);
        if (isOk(res.data)) {
            toast?.info(t('BOT_PLUGINS_SAVED'));
            await fetchAll();
        }
    };

    const enabledSites = createMemo(() => sites().filter(s => s.isEnabled));
    const utcMidnight = () => Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate());

    const classifyState = (s: VoteStateDto | undefined): SiteStatusKind => {
        if (!s) return 'pending';
        if (s.isStale) return 'stale';
        if (!s.lastVoteUtc) return 'pending';
        if (new Date(s.lastVoteUtc).getTime() < utcMidnight()) return 'pending';
        return isPositiveOutcome(s.lastOutcome) ? 'success' : 'failed';
    };

    const siteRows = createMemo<UnifiedSiteStatus[]>(() => {
        const stateMap = new Map<string, VoteStateDto>();
        for (const s of states()) stateMap.set(s.siteKey, s);
        return enabledSites().map(site => {
            const s = stateMap.get(site.key);
            return {
                siteKey: site.key,
                siteDisplayName: site.displayName,
                kind: classifyState(s),
                lastVoteUtc: s?.lastVoteUtc ?? null,
                lastOutcome: s?.lastOutcome ?? 9,
            };
        });
    });

    const redemptionState = createMemo<RedemptionState>(() => {
        const dayStart = utcMidnight();
        let successesToday = 0;
        let redeemed = 0;
        for (const s of states()) {
            if (!s.lastVoteUtc) continue;
            if (new Date(s.lastVoteUtc).getTime() < dayStart) continue;
            if (!isPositiveOutcome(s.lastOutcome)) continue;
            successesToday++;
            if (s.lastRedeemedUtc && new Date(s.lastRedeemedUtc).getTime() >= new Date(s.lastVoteUtc).getTime()) redeemed++;
        }
        if (successesToday === 0) return 'nothingToRedeem';
        return redeemed >= successesToday ? 'allDone' : 'pending';
    });

    const summary = createMemo(() => {
        let succeeded = 0, pending = 0, failed = 0, stale = 0;
        for (const r of siteRows()) {
            if (r.kind === 'success') succeeded++;
            else if (r.kind === 'pending') pending++;
            else if (r.kind === 'failed') failed++;
            else if (r.kind === 'stale') stale++;
        }
        return { succeeded, pending, failed, stale, total: siteRows().length };
    });

    const setVoting = async (enable: boolean) => {
        const b = bot();
        if (!b) return;
        setBusy(true);
        const res = await api.botRegistry.setVoting(b.id, { enabled: enable });
        setBusy(false);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('VOTING_TOGGLE_FAILED'));
            return;
        }
        toast?.info(enable ? t('VOTING_BOT_ENABLED') : t('VOTING_BOT_DISABLED'));
        refreshLicenses();
        await refreshActiveBot();
        await fetchAll();
    };

    const StatusDot = (props: { kind: SiteStatusKind }) => {
        const colorMap: Record<SiteStatusKind, string> = {
            success: 'var(--success-500)',
            pending: 'var(--dark-text-muted)',
            failed: 'var(--error-500)',
            stale: 'var(--warning-500)',
        };
        const iconMap: Record<SiteStatusKind, string> = {
            success: 'fa-check',
            pending: 'fa-clock',
            failed: 'fa-xmark',
            stale: 'fa-pause',
        };
        return (
            <span style={{
                display: 'inline-flex',
                'align-items': 'center',
                'justify-content': 'center',
                width: '1.5rem', height: '1.5rem',
                'border-radius': '50%',
                background: `color-mix(in srgb, ${colorMap[props.kind]} 18%, transparent)`,
                color: colorMap[props.kind],
                'font-size': 'var(--font-size-xs)',
            }}>
                <i class={`fa-solid ${iconMap[props.kind]}`} />
            </span>
        );
    };

    const SiteRow = (props: { row: UnifiedSiteStatus }) => (
        <div class="h" style={{
            'align-items': 'center',
            gap: 'var(--spacing-md)',
            padding: 'var(--spacing-xs) var(--spacing-sm)',
            'border-radius': 'var(--radius-md)',
        }}>
            <StatusDot kind={props.row.kind} />
            <div class="v" style={{ flex: 1, gap: '2px', 'min-width': 0 }}>
                <span style={{ 'font-weight': 500, 'font-size': 'var(--font-size-sm)' }}>{props.row.siteDisplayName}</span>
                <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-xs)' }}>
                    <Show when={props.row.lastVoteUtc} fallback={
                        <Show when={props.row.kind === 'stale'} fallback={<Text val="VOTING_NEVER_VOTED" />}>
                            <Text val="VOTING_STALE_HINT" />
                        </Show>
                    }>
                        {formatDateTime(props.row.lastVoteUtc!)} · <Text val={voteOutcomeKey(props.row.lastOutcome)} />
                    </Show>
                </span>
            </div>
        </div>
    );

    const SummaryChips = () => {
        const r = summary();
        return (
            <div class="h" style={{ gap: 'var(--spacing-sm)', 'flex-wrap': 'wrap', 'font-size': 'var(--font-size-sm)' }}>
                <span style={{ color: 'var(--success-500)', 'font-weight': 600 }}>{r.succeeded} / {r.total}</span>
                <Show when={r.pending > 0}>
                    <span style={{ color: 'var(--dark-text-muted)' }}>· {r.pending} <Text val="VOTING_PENDING" /></span>
                </Show>
                <Show when={r.failed > 0}>
                    <span style={{ color: 'var(--error-500)' }}>· {r.failed} <Text val="VOTING_FAILED" /></span>
                </Show>
                <Show when={r.stale > 0}>
                    <span style={{ color: 'var(--warning-500)' }}>· {r.stale} <Text val="VOTING_STALE" /></span>
                </Show>
            </div>
        );
    };

    const RedemptionBadge = () => (
        <Show when={redemptionState() === 'allDone'}>
            <span class={styles.outcomeBadge} style={{ background: 'color-mix(in srgb, var(--success-500) 18%, transparent)', color: 'var(--success-500)', display: 'inline-flex', 'align-items': 'center', gap: '4px' }}>
                <i class="fa-solid fa-gift" /> <Text val="VOTING_REDEEMED" />
            </span>
        </Show>
    );

    const RedemptionPending = () => (
        <Show when={redemptionState() === 'pending'}>
            <span class={styles.outcomeBadge} style={{ background: 'color-mix(in srgb, var(--warning-500) 18%, transparent)', color: 'var(--warning-500)', display: 'inline-flex', 'align-items': 'center', gap: '4px' }}>
                <i class="fa-solid fa-clock" /> <Text val="VOTING_REDEEM_PENDING" />
            </span>
        </Show>
    );

    const votingEnabled = () => (bot() as any)?.votingEnabled === true;

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="VOTING_BOT_TITLE" /></h1>
            </div>

            <p style={{ color: 'var(--dark-text-muted)', margin: 0, 'font-size': 'var(--font-size-sm)' }}>
                <Text val="VOTING_HELP" />
            </p>

            <Show when={bot()}>
                {(b) => (
                    <div class="fcard v" style={{ gap: 'var(--spacing-md)', 'margin-top': 'var(--spacing-lg)' }}>
                        <div class="h" style={{ 'align-items': 'center', gap: 'var(--spacing-md)', 'flex-wrap': 'wrap' }}>
                            <i class="fa-solid fa-robot" style={{ color: 'var(--primary-400)', 'font-size': 'var(--font-size-lg)' }} />
                            <span style={{ 'font-weight': 600, 'font-size': 'var(--font-size-lg)' }}>{b().minecraftUsername}</span>
                            <BotStatusBadge state={b().state as any} />
                            <Show when={votingEnabled()}>
                                <RedemptionBadge />
                                <RedemptionPending />
                            </Show>
                            <div class="h" style={{ 'margin-left': 'auto', gap: 'var(--spacing-sm)' }}>
                                <button class="btn btn-secondary btn-sm" onClick={() => setShowSettings(true)} disabled={!pluginSettings()}>
                                    <i class="fa-solid fa-gear" /> <Text val="VOTING_BOT_OPEN_SETTINGS" />
                                </button>
                                <button
                                    class={`btn btn-sm ${votingEnabled() ? 'btn-secondary' : 'btn-primary'}`}
                                    disabled={busy()}
                                    onClick={() => void setVoting(!votingEnabled())}
                                >
                                    <Show when={busy()} fallback={
                                        <Text val={votingEnabled() ? 'VOTING_TURN_OFF' : 'VOTING_TURN_ON'} />
                                    }>
                                        <i class="fa-solid fa-spinner fa-spin" />
                                    </Show>
                                </button>
                            </div>
                        </div>

                        <Show when={votingEnabled()} fallback={
                            <div style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>
                                <Text val="VOTING_OFF_HINT" />
                            </div>
                        }>
                            <SummaryChips />
                            <div class="v" style={{ gap: '2px', 'border-top': '1px solid var(--dark-border)', 'padding-top': 'var(--spacing-sm)' }}>
                                <For each={siteRows()}>{(r) => <SiteRow row={r} />}</For>
                            </div>
                        </Show>
                    </div>
                )}
            </Show>

            <Modal
                open={showSettings() && !!pluginSettings()}
                onClose={() => setShowSettings(false)}
                title={<Text val="VOTING_BOT_SETTINGS_TITLE" />}
                maxWidth="640px"
            >
                <Show when={pluginSettings()}>
                    <PluginAutoSettingsForm
                        botId={botId()!}
                        pluginName={PLUGIN_NAME}
                        definitions={pluginSettings()!.definitions}
                        values={edits()}
                        onChange={setEdits}
                        onSave={() => void saveSettings()}
                        onReset={() => fetchPluginSettings()}
                        saving={saving()}
                    />
                </Show>
            </Modal>
        </div>
    );
}
